/**
 * CommunityTemplates.tsx
 * Browse and discover community-shared review templates.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Rating,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as CloneIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  NewReleases as NewIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import api from '../services/api';

interface Template {
  id: number;
  name: string;
  icon_name: string;
  consultant_owner: {
    id: number;
    name: string;
  };
  community_rating: number;
  usage_count: number;
  question_count: number;
  key_focus_areas: string | null;
  is_ai_generated: boolean;
  created_at: string;
}

interface PaginatedResponse {
  results: Template[];
  count: number;
  next: string | null;
  previous: string | null;
}

const CommunityTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'rating' | 'usage' | 'recent'>('rating');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Clone dialog
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTemplate, setViewTemplate] = useState<any>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [searchQuery, sortBy, page]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        type: 'community',
        page: page.toString(),
        page_size: '12',
        ordering: sortBy === 'rating' ? '-community_rating' : sortBy === 'usage' ? '-usage_count' : '-created_at',
      });
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await api.get(`/audit/series/?${params.toString()}`);
      
      // Handle both paginated and non-paginated responses
      if (response.data.results) {
        setTemplates(response.data.results);
        setTotalPages(Math.ceil(response.data.count / 12));
      } else if (Array.isArray(response.data)) {
        setTemplates(response.data);
        setTotalPages(1);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCloneDialog = (template: Template) => {
    setSelectedTemplate(template);
    setNewTemplateName(`${template.name} (Copy)`);
    setCloneDialogOpen(true);
  };

  const handleCloneTemplate = async () => {
    if (!selectedTemplate || !newTemplateName.trim()) return;
    
    try {
      setIsCloning(true);
      setError(null);
      
      await api.post(`/audit/templates/${selectedTemplate.id}/clone/`, {
        name: newTemplateName.trim(),
      });
      
      setSuccess(`Template "${newTemplateName}" created successfully!`);
      setCloneDialogOpen(false);
      setSelectedTemplate(null);
      setNewTemplateName('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to clone template');
    } finally {
      setIsCloning(false);
    }
  };

  const handleViewTemplate = async (template: Template) => {
    try {
      setIsLoadingTemplate(true);
      setViewDialogOpen(true);
      
      const response = await api.get(`/audit/templates/${template.id}/`);
      setViewTemplate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load template details');
      setViewDialogOpen(false);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const getIconComponent = (iconName: string) => {
    // Return a default icon based on name
    return <AssessmentIcon />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Community Templates
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover and clone review templates shared by the community
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => {
                  setSortBy(e.target.value as 'rating' | 'usage' | 'recent');
                  setPage(1);
                }}
              >
                <MenuItem value="rating">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon fontSize="small" />
                    Highest Rated
                  </Box>
                </MenuItem>
                <MenuItem value="usage">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingIcon fontSize="small" />
                    Most Used
                  </Box>
                </MenuItem>
                <MenuItem value="recent">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NewIcon fontSize="small" />
                    Recently Added
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: { md: 'right' } }}>
              {templates.length} templates found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Templates Grid */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <AssessmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No community templates found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'Try adjusting your search criteria' : 'Be the first to share a template with the community!'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={template.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 48,
                          height: 48,
                        }}
                      >
                        {getIconComponent(template.icon_name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          by {template.consultant_owner?.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Rating and Stats */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating
                        value={template.community_rating}
                        readOnly
                        precision={0.5}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        ({template.community_rating?.toFixed(1) || '0.0'})
                      </Typography>
                    </Box>

                    {/* Chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      <Chip
                        size="small"
                        label={`${template.question_count || 0} questions`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`${template.usage_count || 0} uses`}
                        variant="outlined"
                        icon={<TrendingIcon sx={{ fontSize: 14 }} />}
                      />
                      {template.is_ai_generated && (
                        <Chip
                          size="small"
                          label="AI Generated"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Focus Areas Preview */}
                    {template.key_focus_areas && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {template.key_focus_areas.split('\n').slice(0, 2).join(', ')}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Tooltip title="Preview template">
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewTemplate(template)}
                      >
                        Preview
                      </Button>
                    </Tooltip>
                    <Tooltip title="Clone to your templates">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CloneIcon />}
                        onClick={() => handleOpenCloneDialog(template)}
                      >
                        Clone
                      </Button>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a copy of "{selectedTemplate?.name}" in your templates.
          </Typography>
          <TextField
            fullWidth
            label="New Template Name"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCloneTemplate}
            disabled={isCloning || !newTemplateName.trim()}
            startIcon={isCloning ? <CircularProgress size={20} /> : <CloneIcon />}
          >
            Clone
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isLoadingTemplate ? 'Loading...' : viewTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {isLoadingTemplate ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : viewTemplate ? (
            <Box>
              {/* Template Info */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Rating value={viewTemplate.community_rating} readOnly precision={0.5} />
                  <Typography variant="body2" color="text.secondary">
                    ({viewTemplate.community_rating?.toFixed(1)})
                  </Typography>
                  <Chip size="small" label={`${viewTemplate.usage_count} uses`} />
                </Box>
                
                {viewTemplate.key_focus_areas && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Key Focus Areas:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {viewTemplate.key_focus_areas.split('\n').map((area: string, idx: number) => (
                        <Chip key={idx} label={area.trim()} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Questions Preview */}
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Questions ({viewTemplate.questions?.length || 0}):
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {viewTemplate.questions?.slice(0, 10).map((q: any, idx: number) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {idx + 1}. {q.header || q.name}
                    </Typography>
                    {q.details && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {q.details.length} sub-questions
                      </Typography>
                    )}
                  </Paper>
                ))}
                {(viewTemplate.questions?.length || 0) > 10 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                    ... and {viewTemplate.questions.length - 10} more questions
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {viewTemplate && (
            <Button
              variant="contained"
              startIcon={<CloneIcon />}
              onClick={() => {
                setViewDialogOpen(false);
                handleOpenCloneDialog(viewTemplate);
              }}
            >
              Clone This Template
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" variant="filled">
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommunityTemplates;

